import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations, LANGUAGES, getPositioning } from '../i18n/translations';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Check URL parameter first
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get('lang');
    if (urlLang && translations[urlLang]) return urlLang;

    // Try to get saved language from localStorage
    const saved = localStorage.getItem('kaesthetic-language');
    if (saved && translations[saved]) return saved;
    
    // Try to detect browser language
    const browserLang = navigator.language?.split('-')[0];
    if (browserLang && translations[browserLang]) return browserLang;
    
    return 'en'; // Default to English
  });

  useEffect(() => {
    const syncLangFromURL = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const urlLang = urlParams.get('lang');
      if (urlLang && translations[urlLang]) {
        setLanguage(urlLang);
      }
    };
    syncLangFromURL(); // run on mount
    window.addEventListener('popstate', syncLangFromURL);
    return () => window.removeEventListener('popstate', syncLangFromURL);
  }, []); // no dependencies

  useEffect(() => {
    localStorage.setItem('kaesthetic-language', language);
    // Update document lang attribute for accessibility
    document.documentElement.lang = language;
  }, [language]);

  const t = translations[language] || translations['en'];
  const positioning = getPositioning(language);
  const currentLanguage = LANGUAGES.find(l => l.code === language) || LANGUAGES.find(l => l.code === 'en');

  const changeLanguage = (langCode) => {
    if (translations[langCode]) {
      setLanguage(langCode);
    }
  };

  return (
    <LanguageContext.Provider value={{
      language,
      t,
      positioning,
      currentLanguage,
      changeLanguage,
      languages: LANGUAGES
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;